import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Upload, FileText, X, Download, Languages, Clock, Send, Loader2,
  CheckCircle, AlertCircle, Eye, Sparkles, UserCheck, FileEdit, Bot,
  PackageCheck, Trash2, CreditCard
} from 'lucide-react';
import AddonEntitlements from './AddonEntitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const FALLBACK_LANGUAGES = ['English', 'French', 'Spanish (Spain)', 'German', 'Italian'];

function computeQuote(rates, source, target, wordCount, type) {
  if (type === 'ai') {
    return { available: false, perWord: null, total: null, message: 'AI Translation pricing will be calculated separately.' };
  }
  if (!source || !target || source === target) {
    return { available: false, perWord: null, total: null, message: 'Pick a source and target language.' };
  }
  let perWord = null;
  if (source === 'English') perWord = rates[target];
  else if (target === 'English') perWord = rates[source];
  else {
    const a = rates[source]; const b = rates[target];
    if (a != null && b != null) perWord = +(a + b).toFixed(4);
  }
  if (perWord == null) {
    return { available: false, perWord: null, total: null, message: 'Pricing unavailable for this language pair. Our team will review manually.' };
  }
  const total = wordCount ? +(wordCount * perWord).toFixed(2) : null;
  return { available: true, perWord, total, message: null };
}

function formatUsd(n) {
  if (n == null) return '-';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TYPE_OPTIONS = [
  { value: 'ai', label: 'AI Translation', desc: 'Automated machine translation. Fastest turnaround.' },
  { value: 'ai_human', label: 'AI + Human Review', desc: 'AI draft reviewed by a professional legal translator.' }
];

const URGENCY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'urgent', label: 'Urgent' }
];

const STATUS_CONFIG = {
  pending_payment: { label: 'Pending Payment', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertCircle },
  submitted: { label: 'Submitted', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Send },
  ai_processing: { label: 'AI Processing', cls: 'bg-violet-100 text-violet-700 border-violet-200', icon: Loader2 },
  submitted_to_vendor: { label: 'Submitted to Vendor', cls: 'bg-sky-100 text-sky-700 border-sky-200', icon: UserCheck },
  in_review: { label: 'In Review', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Eye },
  ready_for_download: { label: 'Ready for Download', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: PackageCheck },
  completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle },
  changes_requested: { label: 'Changes Requested', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertCircle }
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileEdit };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.cls}`} data-testid={`translation-status-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function downloadBase64(fileName, base64, mime = 'application/octet-stream') {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const MIME_BY_EXT = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain'
};

async function downloadFileById(fileId) {
  const res = await axios.get(`${API}/translations/file/${fileId}`, { headers: authHeaders() });
  const { file_name, file_type, data } = res.data;
  downloadBase64(file_name, data, MIME_BY_EXT[file_type] || 'application/octet-stream');
}

// ────────────────────────────────────────────────────────────────
// New Request Tab (client + admin/team can create on behalf of firm)
// ────────────────────────────────────────────────────────────────
function NewRequestTab({ firmId, onCreated, canCreate, user, rates, languages }) {
  const [files, setFiles] = useState([]); // {id, file_name, file_type, estimated_word_count}
  const [uploading, setUploading] = useState(false);
  const [sourceLang, setSourceLang] = useState('English');
  const [targetLang, setTargetLang] = useState('French');
  const [translationType, setTranslationType] = useState('ai_human');
  const [urgency, setUrgency] = useState('standard');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const totalWords = useMemo(() => files.reduce((s, f) => s + (f.estimated_word_count || 0), 0), [files]);
  const hasUnknown = files.some(f => f.estimated_word_count === null || f.estimated_word_count === undefined);
  const quote = useMemo(
    () => computeQuote(rates, sourceLang, targetLang, totalWords, translationType),
    [rates, sourceLang, targetLang, totalWords, translationType]
  );

  const handleFiles = async (selected) => {
    setError('');
    setUploading(true);
    const next = [...files];
    for (const f of selected) {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
        setError(`Unsupported file: ${f.name}. Allowed: PDF, DOC, DOCX, TXT`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      fd.append('file_role', 'original');
      try {
        const res = await axios.post(`${API}/translations/upload-file`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
        });
        next.push(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Upload failed');
      }
    }
    setFiles(next);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id) => setFiles(files.filter(f => f.id !== id));

  const submit = async () => {
    setError('');
    if (!files.length) { setError('Please upload at least one document.'); return; }
    if (!sourceLang || !targetLang) { setError('Please select source and target languages.'); return; }
    if (sourceLang === targetLang) { setError('Source and target languages must be different.'); return; }
    // For paid (AI + Human Review) requests, require a valid quote BEFORE submitting
    if (translationType === 'ai_human' && (!quote.available || !quote.total || quote.total <= 0)) {
      setError(quote.message || 'Pricing not available. Our team will provide a manual quote - please contact support.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/translations/${firmId}`, {
        source_language: sourceLang,
        target_language: targetLang,
        translation_type: translationType,
        urgency,
        notes,
        files: files.map(f => ({
          id: f.id,
          file_name: f.file_name,
          file_type: f.file_type,
          estimated_word_count: f.estimated_word_count,
          file_role: 'original'
        }))
      }, { headers: authHeaders() });

      // For AI + Human Review: redirect to Stripe Checkout immediately.
      // For AI: legacy path - request is already submitted.
      if (translationType === 'ai_human') {
        const reqId = res.data?.id;
        if (!reqId) {
          setError('Could not create translation request.');
          return;
        }
        try {
          const co = await axios.post(
            `${API}/stripe/create-translation-checkout-session`,
            { translation_request_id: reqId },
            { headers: authHeaders() }
          );
          if (co.data?.url) {
            window.location.href = co.data.url;
            return;
          }
          setError('Payment session could not be created. Please try again.');
        } catch (e) {
          setError(e.response?.data?.detail || 'Payment session could not be created. Please try again.');
        }
        // Refresh list so the pending_payment row shows up
        onCreated?.();
        return;
      }
      // AI path - reset and reload
      setFiles([]); setNotes(''); setUrgency('standard');
      onCreated?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center text-slate-500 border border-slate-200" data-testid="translation-readonly-msg">
        Only the client can create new translation requests for this firm.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="translation-new-request-form">
      {/* Upload box */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Upload Document(s)</h3>
        <p className="text-xs text-slate-500 mb-4">PDF, DOC, DOCX, TXT - up to 10MB each. Multiple files allowed.</p>

        <label className="flex flex-col items-center justify-center border-2 border-dashed border-violet-300 hover:border-violet-500 bg-violet-50/40 hover:bg-violet-50 rounded-2xl py-10 px-6 cursor-pointer transition-colors" data-testid="translation-file-dropzone">
          <Upload className="w-8 h-8 text-violet-500 mb-2" />
          <span className="text-sm font-medium text-slate-700">{uploading ? 'Uploading...' : 'Click to upload or drag files here'}</span>
          <span className="text-xs text-slate-500 mt-1">Word count is calculated automatically when possible.</span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt"
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            data-testid="translation-file-input"
          />
        </label>

        {files.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="translation-uploaded-files">
            {files.map(f => (
              <div key={f.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{f.file_name}</div>
                    <div className="text-xs text-slate-500">
                      {f.estimated_word_count !== null && f.estimated_word_count !== undefined
                        ? `Estimated Word Count: ${f.estimated_word_count.toLocaleString()} words`
                        : 'Word count could not be calculated automatically. Team will review manually.'}
                    </div>
                  </div>
                </div>
                <button onClick={() => removeFile(f.id)} className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500" data-testid={`remove-file-${f.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm">
              <span className="font-medium text-slate-700">Total estimated word count</span>
              <span className="font-bold text-violet-700" data-testid="translation-total-word-count">
                {totalWords.toLocaleString()} words{hasUnknown ? ' + manual review' : ''}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Languages */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <label className="text-sm font-semibold text-slate-900">Source Language</label>
          <select
            value={sourceLang}
            onChange={(e) => setSourceLang(e.target.value)}
            className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            data-testid="translation-source-lang"
          >
            {(languages || FALLBACK_LANGUAGES).map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <label className="text-sm font-semibold text-slate-900">Target Language</label>
          <select
            value={targetLang}
            onChange={(e) => setTargetLang(e.target.value)}
            className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            data-testid="translation-target-lang"
          >
            {(languages || FALLBACK_LANGUAGES).map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {/* Translation type */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <label className="text-sm font-semibold text-slate-900 mb-3 block">Translation Type</label>
        <div className="grid md:grid-cols-2 gap-3">
          {TYPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTranslationType(opt.value)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${translationType === opt.value ? 'border-violet-500 bg-violet-50/60 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
              data-testid={`translation-type-${opt.value}`}
            >
              <div className="flex items-center gap-2">
                {opt.value === 'ai' ? <Bot className="w-4 h-4 text-violet-600" /> : <UserCheck className="w-4 h-4 text-violet-600" />}
                <span className="font-medium text-slate-900 text-sm">{opt.label}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
            </button>
          ))}
        </div>
        {translationType === 'ai' && (
          <div className="mt-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800" data-testid="ai-translation-note">
            <strong>Heads up:</strong> Your AI translation request will be submitted. Automated translation processing will be connected later.
          </div>
        )}
      </div>

      {/* Urgency */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <label className="text-sm font-semibold text-slate-900 mb-3 block">Urgency</label>
        <div className="flex gap-3">
          {URGENCY_OPTIONS.map(u => (
            <button
              key={u.value}
              type="button"
              onClick={() => setUrgency(u.value)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border ${urgency === u.value ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
              data-testid={`urgency-${u.value}`}
            >
              {u.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <label className="text-sm font-semibold text-slate-900">Notes / Instructions (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Anything our translators should know? Terminology preferences, deadlines, etc."
          className="mt-2 w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
          data-testid="translation-notes"
        />
      </div>

      {/* Pricing card (AI + Human Review only) */}
      <div className={`rounded-2xl border p-5 transition-all ${quote.available ? 'bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-200' : 'bg-amber-50 border-amber-200'}`} data-testid="pricing-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${quote.available ? 'bg-violet-600' : 'bg-amber-500'}`}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900">Estimated Pricing</h4>
              <p className="text-xs text-slate-600">{translationType === 'ai_human' ? 'AI + Human Review · USD' : 'AI Translation'}</p>
            </div>
          </div>
          {quote.available && (
            <div className="text-right">
              <div className="text-3xl font-bold text-violet-700" data-testid="pricing-total">
                {formatUsd(quote.total)}
              </div>
              <div className="text-xs text-slate-500">Estimated total</div>
            </div>
          )}
        </div>
        {quote.available ? (
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-violet-100">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Language Pair</div>
              <div className="text-sm font-semibold text-slate-900 mt-1" data-testid="pricing-language-pair">{sourceLang} → {targetLang}</div>
            </div>
            <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-violet-100">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Word Count</div>
              <div className="text-sm font-semibold text-slate-900 mt-1" data-testid="pricing-word-count">
                {totalWords ? totalWords.toLocaleString() : '-'}
                {hasUnknown && <span className="text-xs font-normal text-amber-600 ml-1">+ manual</span>}
              </div>
            </div>
            <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-violet-100">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Per Word Rate</div>
              <div className="text-sm font-semibold text-slate-900 mt-1" data-testid="pricing-per-word">
                ${quote.perWord?.toFixed(4)}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-sm text-amber-800" data-testid="pricing-message">
            {quote.message}
          </div>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm" data-testid="translation-error">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          onClick={submit}
          disabled={submitting || uploading || !files.length}
          className="px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold text-sm shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="translation-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (translationType === 'ai_human' ? <CreditCard className="w-4 h-4" /> : <Send className="w-4 h-4" />)}
          {submitting
            ? (translationType === 'ai_human' ? 'Redirecting to payment…' : 'Submitting...')
            : (translationType === 'ai_human' ? 'Pay & Submit Translation' : 'Submit Translation Request')}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// My Requests Tab
// ────────────────────────────────────────────────────────────────
function RequestsListTab({ requests, loading, onRefresh }) {
  const [retrying, setRetrying] = useState(null);
  const completePayment = async (reqId) => {
    setRetrying(reqId);
    try {
      const res = await axios.post(
        `${API}/stripe/create-translation-checkout-session`,
        { translation_request_id: reqId },
        { headers: authHeaders() }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      alert('Payment session could not be created. Please try again.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Payment session could not be created. Please try again.');
    } finally {
      setRetrying(null);
    }
  };
  if (loading) return <div className="text-center py-10 text-slate-500">Loading...</div>;
  if (!requests.length) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="translation-empty">
        <Languages className="w-10 h-10 text-violet-400 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-900">No translation requests yet</h3>
        <p className="text-sm text-slate-500 mt-1">Use the New Request tab to upload your first document.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="translation-requests-list">
      {requests.map(r => (
        <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-violet-300 transition-colors">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-mono text-slate-500" data-testid={`request-id-${r.id.slice(0, 8)}`}>#{r.id.slice(0, 8)}</span>
                <StatusBadge status={r.status} />
                {r.urgency === 'urgent' && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-medium">Urgent</span>
                )}
              </div>
              <h3 className="text-base font-semibold text-slate-900 mt-2">
                {r.source_language} <span className="text-slate-400">→</span> {r.target_language}
              </h3>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  {r.translation_type === 'ai' ? <Bot className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                  {r.translation_type === 'ai' ? 'AI Translation' : 'AI + Human Review'}
                </span>
                <span>•</span>
                <span>{r.total_word_count ? `${r.total_word_count.toLocaleString()} words` : 'Manual review'}</span>
                {r.translation_type === 'ai_human' && r.per_word_rate_usd != null && (
                  <>
                    <span>•</span>
                    <span data-testid={`request-rate-${r.id.slice(0, 8)}`}>${r.per_word_rate_usd.toFixed(4)}/word</span>
                    {r.estimated_total_usd != null && (
                      <>
                        <span>•</span>
                        <span className="font-semibold text-violet-700" data-testid={`request-total-${r.id.slice(0, 8)}`}>{formatUsd(r.estimated_total_usd)}</span>
                      </>
                    )}
                  </>
                )}
                {r.translation_type === 'ai_human' && r.per_word_rate_usd == null && r.pricing_message && (
                  <>
                    <span>•</span>
                    <span className="text-amber-600">Manual quote</span>
                  </>
                )}
                <span>•</span>
                <span>{(r.files || []).length} file(s)</span>
                <span>•</span>
                <span>{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.notes && (
                <p className="text-xs text-slate-600 mt-2 line-clamp-2"><span className="font-medium text-slate-700">Notes:</span> {r.notes}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {r.status === 'pending_payment' && (
                <button
                  onClick={() => completePayment(r.id)}
                  disabled={retrying === r.id}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`complete-payment-${r.id.slice(0, 8)}`}
                >
                  {retrying === r.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <CreditCard className="w-4 h-4" />}
                  {retrying === r.id ? 'Redirecting…' : 'Complete Payment'}
                </button>
              )}
              {(r.files || []).filter(f => f.file_role === 'translated').length > 0 && (
                <button
                  onClick={() => (r.files || []).filter(f => f.file_role === 'translated').forEach(f => downloadFileById(f.id))}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700"
                  data-testid={`download-translated-${r.id.slice(0, 8)}`}
                >
                  <Download className="w-4 h-4" />
                  Download Translated
                </button>
              )}
              <button
                onClick={() => (r.files || []).filter(f => f.file_role === 'original').forEach(f => downloadFileById(f.id))}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-200"
                data-testid={`download-original-${r.id.slice(0, 8)}`}
              >
                <Download className="w-3.5 h-3.5" />
                Original
              </button>
            </div>
          </div>
        </div>
      ))}
      <div className="text-right">
        <button onClick={onRefresh} className="text-xs text-violet-600 hover:text-violet-700 font-medium" data-testid="translation-refresh">
          Refresh
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Downloads Tab - completed/ready translations only
// ────────────────────────────────────────────────────────────────
function DownloadsTab({ requests }) {
  const ready = useMemo(() => requests.filter(r => ['ready_for_download', 'completed'].includes(r.status)), [requests]);
  if (!ready.length) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="translation-no-downloads">
        <PackageCheck className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-900">No completed translations yet</h3>
        <p className="text-sm text-slate-500 mt-1">When your translated files are ready, you'll find them here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="translation-downloads-list">
      {ready.map(r => (
        <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-xs font-mono text-slate-500">#{r.id.slice(0, 8)}</div>
              <h4 className="text-sm font-semibold text-slate-900 mt-1">
                {r.source_language} <span className="text-slate-400">→</span> {r.target_language}
              </h4>
              <div className="text-xs text-slate-500 mt-1">
                Completed {r.completed_at ? new Date(r.completed_at).toLocaleString() : '-'}
              </div>
            </div>
            <StatusBadge status={r.status} />
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-2">
            {(r.files || []).map(f => (
              <button
                key={f.id}
                onClick={() => downloadFileById(f.id)}
                className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${f.file_role === 'translated' ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                data-testid={`download-file-${f.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className={`w-4 h-4 ${f.file_role === 'translated' ? 'text-emerald-700' : 'text-slate-500'}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{f.file_name}</div>
                    <div className="text-xs text-slate-500 capitalize">{f.file_role}</div>
                  </div>
                </div>
                <Download className={`w-4 h-4 ${f.file_role === 'translated' ? 'text-emerald-700' : 'text-slate-500'}`} />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────
export default function LegalTranslations({ user, selectedClient }) {
  const firmId = selectedClient?.firmId || user.firm?.id;
  const [tab, setTab] = useState('new');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState({});
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [paymentNotice, setPaymentNotice] = useState(null); // 'cancelled' | 'success' | null

  const canCreate = user.role === 'client' || (user.role !== 'client' && !!selectedClient);

  // Read ?payment= from URL on mount and clear the query string so the banner
  // doesn't re-show on every refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const p = url.searchParams.get('payment');
    if (p === 'cancelled' || p === 'success') {
      setPaymentNotice(p);
      // Default the user to the list tab when returning from Stripe
      setTab('list');
      url.searchParams.delete('payment');
      window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams}` : ''));
    }
  }, []);

  const refresh = async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/translations/${firmId}`, { headers: authHeaders() });
      setRequests(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [firmId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/translation-rates`, { headers: authHeaders() });
        setRates(res.data?.rates || {});
        setLanguages(res.data?.languages || FALLBACK_LANGUAGES);
      } catch (e) {
        console.error('Rates fetch failed', e);
      }
    })();
  }, []);

  const seedSample = async () => {
    try {
      await axios.post(`${API}/translations/seed/${firmId}`, {}, { headers: authHeaders() });
      refresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6" data-testid="legal-translations-module">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-500/30">
              <Languages className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Legal Translations</h1>
            <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wide">Add-on</span>
          </div>
          <p className="text-sm text-slate-500">Upload legal documents, get AI or human-reviewed translations, and download when ready.</p>
        </div>
        <AddonEntitlements firmId={firmId} addonKey="legal_translations" />
        {user.role !== 'client' && requests.length === 0 && firmId && (
          <button onClick={seedSample} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="translation-seed-btn">
            <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Load sample data
          </button>
        )}
      </div>

      {/* Payment notice banner (shown when returning from Stripe) */}
      {paymentNotice === 'cancelled' && (
        <div className="px-5 py-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3" data-testid="translation-payment-cancelled-notice">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-900 font-medium">
              Your payment was cancelled. Your translation request is still saved as <strong>Pending Payment</strong> - click <em>Complete Payment</em> below to retry.
            </span>
          </div>
          <button onClick={() => setPaymentNotice(null)} className="text-amber-700 hover:text-amber-900 text-sm font-semibold">Dismiss</button>
        </div>
      )}
      {paymentNotice === 'success' && (
        <div className="px-5 py-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between gap-3" data-testid="translation-payment-success-notice">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            <span className="text-sm text-emerald-900 font-medium">Payment confirmed. Your translation request has been submitted to our vendor team.</span>
          </div>
          <button onClick={() => setPaymentNotice(null)} className="text-emerald-700 hover:text-emerald-900 text-sm font-semibold">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit" role="tablist" data-testid="translation-tabs">
        {[
          { id: 'new', label: 'New Request', icon: Plus },
          { id: 'list', label: 'My Translation Requests', icon: FileText },
          { id: 'downloads', label: 'Downloads', icon: Download }
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${tab === t.id ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'new' && (
        <NewRequestTab
          firmId={firmId}
          canCreate={canCreate}
          user={user}
          rates={rates}
          languages={languages}
          onCreated={() => { refresh(); setTab('list'); }}
        />
      )}
      {tab === 'list' && <RequestsListTab requests={requests} loading={loading} onRefresh={refresh} />}
      {tab === 'downloads' && <DownloadsTab requests={requests} />}
    </div>
  );
}
